import React, { useEffect, useState } from 'react';
import { PaintBrushIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import ThemeChanger from '../../components/theme-changer/theme-changer';
import Notification from '../../components/notification/notification';
import companyApi from '../../services/companyApi';
import companySettingApi from '../../services/companySettingApi';
import {
  setGlobalCompany,
  fetchCompanySettings,
} from '../../store/slices/companySlice';
import { useSelector, useDispatch } from 'react-redux';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import { PageHeader, IconBadge, ToggleSwitch } from '../../components/ui';
import OrderNotificationRow from './components/order-notification-row';
import {
  readOrderNotificationSettings,
  splitSettingsForSave,
} from './order-notification-settings';

const LocalSectionCard = ({ icon: Icon, tone, title, subtitle, children }) => (
  <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50/60 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/40">
      <IconBadge icon={Icon} tone={tone} size="md" />
      <div>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        )}
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </section>
);

// Both notification rows write the selected COMPANY's settings (weekly report
// recipient, order emails) — nothing here is the admin's own. Admins manage
// companies from Companies → Settings, so their Preferences hides the card.
export const showNotificationsCard = (roles) => !(roles || []).includes(100);

const Preferences = () => {
  const dispatch = useDispatch();
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const roles = useSelector((state) => state.authReducer.roles);
  const companySettings = useSelector(
    (state) => state.companyReducer.settings,
  );

  const [loading, setLoading] = useState({
    companyDetail: false,
    companyUpsert: false,
    orderNotificationSave: false,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [companyDetail, setCompanyDetail] = useState({});

  const getCompanyDetail = async () => {
    try {
      setLoading((prev) => ({ ...prev, companyDetail: true }));
      const res = await companyApi.getById(globalCompany.id);
      if (res.data) setCompanyDetail(res.data.data);
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, companyDetail: false }));
    }
  };

  const onChangeCompanySubmit = async (value, key) => {
    setCompanyDetail((prev) => ({ ...prev, [key]: value }));
    const body = { ...globalCompany, [key]: value };
    const formData = new FormData();
    Object.keys(body).forEach((k) => {
      if (body[k]) formData.append(k, body[k]);
    });
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };
    try {
      setLoading((prev) => ({ ...prev, companyUpsert: true }));
      const res = await companyApi.upsert(formData, config);
      dispatch(setGlobalCompany(res.data.data));
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, companyUpsert: false }));
      setNotf({
        display: true,
        type: 'success',
        message: 'Company updated successfully',
      });
    }
  };

  const onSaveOrderNotificationSettings = async (changes) => {
    try {
      setLoading((prev) => ({ ...prev, orderNotificationSave: true }));
      const { toCreate, toUpdate } = splitSettingsForSave(
        companySettings,
        changes,
      );
      if (toCreate.length > 0) {
        await companySettingApi.batchCreate({
          companyid: globalCompany.id,
          settings: toCreate,
        });
      }
      if (toUpdate.length > 0) {
        await companySettingApi.batchUpdate({
          companyid: globalCompany.id,
          settings: toUpdate,
        });
      }
      setNotf({
        display: true,
        type: 'success',
        message: 'Notification settings saved',
      });
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to save notification settings',
      });
    } finally {
      // Refetch even after a failed write: a partial batch (or a write that
      // landed despite an error response) must not leave the map routing the
      // next save to Create when the row now exists. A refetch failure alone
      // must not repaint a successful save as an error.
      try {
        await dispatch(fetchCompanySettings(globalCompany.id)).unwrap();
      } catch (error) {
        // keep the settings map as-is; the next save retries the fetch
      }
      setLoading((prev) => ({ ...prev, orderNotificationSave: false }));
    }
  };

  useEffect(() => {
    getCompanyDetail();
    // Admins don't see the Notifications card, and a session may have no
    // selected company; only load settings when the row can render.
    if (globalCompany?.id && showNotificationsCard(roles)) {
      dispatch(fetchCompanySettings(globalCompany.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Preferences"
          description="Take control of your dashboard settings and customize your experience."
        />

        {loading.companyDetail ? (
          <section className="overflow-hidden rounded-md border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-10 w-10 !rounded-md" />
              <div className="space-y-2">
                <SkeletonBlock className="h-4 w-32" />
                <SkeletonBlock className="h-3 w-48" />
              </div>
            </div>
            <div className="mt-5 space-y-3">
              <SkeletonBlock className="h-10 w-full" />
              <SkeletonBlock className="h-10 w-2/3" />
            </div>
          </section>
        ) : (
          <div className="space-y-6">
            <LocalSectionCard
              icon={PaintBrushIcon}
              tone="blue"
              title="Appearance"
              subtitle="Switch between light and dark themes"
            >
              <div className="flex items-center justify-between gap-4">
                <ThemeChanger switchMode={true} onChange={() => {}} />
              </div>
            </LocalSectionCard>

            {showNotificationsCard(roles) && (
            <LocalSectionCard
              icon={BellAlertIcon}
              tone="emerald"
              title="Notifications"
              subtitle="Manage what communication you receive"
            >
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                <div
                  className={`flex items-start justify-between gap-4 ${
                    globalCompany?.id ? 'pb-5' : ''
                  }`}
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      Weekly report
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Receive weekly reports on your company&apos;s performance.
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={companyDetail?.sendreport}
                    disabled={loading.companyUpsert}
                    ariaLabel="Weekly report"
                    onChange={(e) => onChangeCompanySubmit(e, 'sendreport')}
                  />
                </div>
                {globalCompany?.id && (
                  <div className="pt-5">
                    <OrderNotificationRow
                      settings={readOrderNotificationSettings(companySettings)}
                      saving={loading.orderNotificationSave}
                      onChange={onSaveOrderNotificationSettings}
                    />
                  </div>
                )}
              </div>
            </LocalSectionCard>
            )}
          </div>
        )}
      </div>
      <Notification
        classAttributes="pt-20"
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Preferences;
